import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Heart, Sparkles, CalendarCheck, Link2, Eye, Send, Gift } from "@/lib/icons";
import { toast } from "sonner";
import { GardenPlant } from "@/components/client/Garden";
import { RewardProfileForm } from "@/components/client/RewardProfile";
import { ModalClose } from "@/components/ui/ModalClose";

const GardenPreview = () => (
  <div className="flex items-end justify-between gap-2 mt-3 rounded-2xl bg-amber-50 border border-amber-100 p-3" data-testid="tour-garden-preview">
    {[0, 3, 6, 9].map((st) => <GardenPlant key={st} stage={st} progress={st === 0 ? 0.3 : 1} className="w-14 h-[72px]" animate={false} />)}
  </div>
);
const TierPreview = () => (
  <div className="flex gap-2 mt-3" data-testid="tour-tier-preview">
    {[["Full", "bg-amber-700 text-white"], ["Partial", "bg-amber-200 text-amber-900"], ["Showed up", "bg-amber-50 text-amber-900 border border-amber-200"]].map(([l, c]) => <span key={l} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${c}`}>{l}</span>)}
  </div>
);

const CLIENT_STEPS = [
  { icon: Sparkles, title: "Welcome to your garden", secs: 10, body: "Every check-in waters your plant. It grows through ten stages — Seedling to Luminary — and nothing ever shrinks it. This is a positive-only space.", extra: GardenPreview },
  { icon: CalendarCheck, title: "Log a habit in ten seconds", secs: 10, body: "On the Today tab, tap a habit and pick how it went. Full, partial, or just showing up — all three count and all three earn points. Missing a day costs nothing.", extra: TierPreview },
  { icon: Heart, title: "Tell us how today felt", secs: 10, body: "\"How's today?\" has quick sliders for mood, sleep, stress and more, plus any custom trackers you or your coach add. Trends appear over weeks — never judgement." },
  { icon: Gift, title: "Rewards made for you", secs: 10, body: "After a check-in you can ask for a small, personal reward. Rate each one (motivating / neutral / deterring) and they get better. Share weekly wins with an anonymous handle if you like." },
  { icon: Sparkles, title: "Make rewards yours", secs: 20, body: "A few lines in your own words — what you love, what feels like a treat, what to steer clear of. The more specific, the more creative your rewards. Your coach sees these too.", profile: true },
];

const CLINICIAN_STEPS = [
  { icon: Link2, title: "Invite the people you support", body: "Your invite code sits at the top right. Tap it to copy the code, or use \"Copy invite link\" — anyone who signs up through it is connected to you straight away, and their access is covered by your plan." },
  { icon: Eye, title: "Flags, trends & the monthly report", body: "Each member card shows habits, wellbeing scales, custom trackers and gentle \"Worth a look\" flags when dissociation, shutdown, panic or low mood persist for days. The monthly summary and CSV export are built for session notes." },
  { icon: Send, title: "Nudge, assign, recommend", body: "Send a short encouragement or recommend a Learn article, assign a custom tracker (\"Took medication\", \"Self-harm urges\"), and watch the AI pattern insights connect habits to wellbeing." },
];

function ProfileStep({ onDone }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/me/reward-profile").then((r) => setP(r.data)); }, []);
  if (!p) return <div className="text-sm text-stone-500 mt-3">Loading…</div>;
  return <div className="mt-3" data-testid="tour-profile-step"><RewardProfileForm profile={p} compact saveLabel="Save & start" onSaved={onDone} /></div>;
}

export function WelcomeTour({ role = "client" }) {
  const { user, setUser } = useAuth();
  const STEPS = role === "clinician" ? CLINICIAN_STEPS : CLIENT_STEPS;
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const done = user?.tours_done?.[role];
  const secsLeft = STEPS.slice(step).reduce((a, s) => a + (s.secs || 10), 0);

  useEffect(() => {
    if (user && done === false) setOpen(true);
  }, [user, done]);

  const finish = async () => {
    setOpen(false);
    setUser((u) => (u ? { ...u, tours_done: { ...(u.tours_done || {}), [role]: true } } : u));
    try {
      await api.post(`/users/tour/${role}/done`);
    } catch (e) {
      console.warn("Could not save tour completion", e?.message);
    }
  };

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="welcome-tour">
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="warm-card relative w-full max-w-md p-7 max-h-[92vh] overflow-y-auto">
          <ModalClose onClose={finish} testid="tour-close" label="Skip tour" />
          <div className="flex items-center justify-between pr-10">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Welcome · step {step + 1} of {STEPS.length}</div>
            <div className="text-[10px] font-mono text-stone-500" data-testid="tour-time-left">~{secsLeft}s left</div>
          </div>
          <div className="mt-2 h-1 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-amber-600 transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
          <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center mt-4"><Icon className="w-6 h-6" /></div>
          <h2 className="font-display text-2xl font-extrabold text-stone-900 mt-3" data-testid="tour-title">{s.title}</h2>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">{s.body}</p>
          {s.extra && <s.extra />}
          {s.profile && <ProfileStep onDone={finish} />}
          <div className="flex items-center justify-between mt-6">
            <button type="button" data-testid="tour-skip" onClick={finish} className="text-xs font-semibold text-stone-500 hover:text-stone-800">{s.profile ? "Skip for now" : "Skip tour"}</button>
            <div className="flex items-center gap-3">
              <div className="flex gap-1">{STEPS.map((s, i) => <span key={s.title || `step-${i}`} className={`w-2 h-2 rounded-full ${i === step ? "bg-amber-600" : "bg-stone-300"}`} />)}</div>
              {step < STEPS.length - 1 ? (
                <button type="button" data-testid="tour-next" onClick={() => setStep(step + 1)} className="amber-btn text-sm inline-flex items-center gap-1">Next <ArrowRight className="w-4 h-4" /></button>
              ) : !s.profile ? (
                <button type="button" data-testid="tour-done" onClick={finish} className="amber-btn text-sm">Let's begin</button>
              ) : null}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
